export type JobPosting = {
  slug: string;
  title: string;
  location: string;
  employmentType: string;
  workArrangement: string;
  hours: string;
  salary: string;
  vacancies: string;
  startDate: string;
  languageRequirement: string;
  posted: { year: number; month: number; day: number };
  applySubject: string;
  /** i18n key for card summary */
  excerptKey: string;
  aboutLead: string;
  opportunity: string;
  responsibilities: string[];
  required: string[];
  niceToHave: string[];
  whatWeOffer: string[];
  howToApplyLead: string;
  howToApplyResumeNote: string;
  howToApplyScreening: string;
  equalOpportunity: string;
};

const seniorCyclosResponsibilities = [
  'Design, develop, integrate, test, and deploy a digital banking and payment platform based on Cyclos.',
  'Deploy, manage, and scale AWS cloud infrastructure, including EC2, S3, and RDS, to support platform availability and performance.',
  'Configure and customize Cyclos features, including accounts, payment types, merchant tools, transaction fees, lending, user roles, and access profiles.',
  'Integrate Cyclos with external banking systems, payment service providers, KYC/AML providers, and payment gateways.',
  'Optimize database queries, API response times, and transaction processing performance.',
  'Implement security controls, including data encryption and secure authentication using OAuth2 and OpenID Connect, and support applicable PCI DSS requirements.',
  'Collaborate with product and compliance teams to translate business and applicable regulatory requirements into software functionality.',
  'Maintain and improve existing software, investigate production incidents, and resolve technical issues.',
  'Conduct code reviews and mentor junior developers.',
  'Maintain technical documentation covering platform architecture, configurations, integrations, and deployment pipelines.',
];

const seniorCyclosRequired = [
  'At least 3 years of professional experience in software development, backend systems, and financial or transactional platforms. No specific degree, certificate, or diploma is required; relevant education is welcome.',
  'Cyclos certification or equivalent proven hands-on experience with the Cyclos platform.',
  'Strong understanding of Cyclos accounts, payment types, transaction processing, user roles, access profiles, and invoicing.',
  'Proficiency in Java and JavaScript/TypeScript, with experience in Angular or React for interface customization.',
  'Experience developing and integrating RESTful APIs, including the Cyclos Web Services API.',
  'Strong knowledge of relational databases, including MySQL and PostgreSQL, and transaction isolation levels.',
  'Hands-on experience deploying and managing applications on AWS.',
  'Familiarity with applicable Canadian financial compliance requirements, including FINTRAC requirements and relevant OSFI guidelines.',
  'Experience with Git, CI/CD pipelines such as Jenkins or GitHub Actions, and Docker.',
  'Ability to work independently, collaborate with colleagues, and take ownership of technical solutions.',
  'Ability to communicate effectively in English or French.',
  'Availability to work on site at our LaSalle, Montréal office.',
];

const seniorCyclosNice = [
  'Experience with instant payment systems, such as Interac e-Transfer.',
  'Knowledge of microservices architecture and message brokers such as Kafka or RabbitMQ.',
  'Ability to communicate in both English and French.',
];

const seniorCyclosOffer = [
  'Base salary of CAD 95,000 per year, plus a performance bonus.',
  'Full-time, permanent employment with 40 working hours per week.',
  'Health, dental, and vision benefits starting on the first day.',
  'RRSP matching program.',
  'A collaborative working environment.',
  'Opportunity to help develop a fintech platform with substantial technical responsibility.',
  'Employer-paid professional development, including relevant Cyclos training and conferences.',
];

export const JOB_POSTINGS: JobPosting[] = [
  {
    slug: 'senior-software-engineer-cyclos',
    title: 'Senior Software Developer (Cyclos Developer) – Digital Payments & Online Banking',
    location: 'LaSalle, Montréal, Quebec, Canada',
    employmentType: 'Full-time, permanent',
    workArrangement: 'On site',
    hours: '40 hours per week',
    salary: 'CAD 95,000 per year',
    vacancies: '1',
    startDate: 'As soon as possible',
    languageRequirement: 'English or French',
    posted: { year: 2026, month: 10, day: 16 },
    applySubject: 'Senior Software Developer (Cyclos Developer) – Canada Office',
    excerptKey: 'careers.jobSeniorCyclos.cardExcerpt',
    aboutLead:
      'Flexy Group is a growing technology company delivering Banking-as-a-Service (BaaS) infrastructure solutions in emerging markets. We are building a secure, scalable financial platform based on Cyclos and are looking for an experienced software developer to support its development, customization, and integration.',
    opportunity:
      'We are seeking a Senior Software Developer with proven hands-on experience in Cyclos digital payment and online banking software to join our Montréal office. You will design, develop, deploy, and maintain Flexy Group’s payment and digital banking platform using Cyclos as its core foundation. You will serve as our internal Cyclos specialist and lead platform customization, external integrations, and performance improvements.',
    responsibilities: seniorCyclosResponsibilities,
    required: seniorCyclosRequired,
    niceToHave: seniorCyclosNice,
    whatWeOffer: seniorCyclosOffer,
    howToApplyLead: 'Send your resume to',
    howToApplyResumeNote:
      'Your resume should describe your relevant software development experience and Cyclos projects, including platform configuration, customization, or API integrations.',
    howToApplyScreening:
      'Following initial screening, candidates may be asked to provide references confirming their experience and relevant work examples. Confidential information from previous employers is not required.',
    equalOpportunity:
      'Flexy Group is an equal opportunity employer committed to an inclusive workplace.',
  },
];

export function getJobBySlug(slug: string | undefined): JobPosting | undefined {
  if (!slug) return undefined;
  return JOB_POSTINGS.find((j) => j.slug === slug);
}

export function formatPostedDateMontreal(
  locale: string,
  year: number,
  month: number,
  day: number
): string {
  const lang = locale?.startsWith('fr') ? 'fr-CA' : 'en-CA';
  const instant = new Date(Date.UTC(year, month - 1, day, 12, 0, 0));
  return new Intl.DateTimeFormat(lang, {
    timeZone: 'America/Montreal',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(instant);
}
